import { numeralFormatter } from 'ns_libs/formatter';
import { IProFormaReturnsData, IWaterfallAnalysis } from 'views/ProFormaTable/types';
import { Format, RowValue } from '../../ExecutiveSummaryGenericTable/types';
import { ReturnsMetricId } from '../../layout/metrics';
import { BlockResolver } from '../../layout/types';

const cell = (value: string | number | undefined, format: Format) => (): RowValue => ({ value: value ?? '-', format });

const equityMultipleCell = (value: string | number | undefined) => (): RowValue => ({
    value: numeralFormatter(value ?? '-', '0,0.00'),
    format: 'none',
    label: 'x',
});

const basisPointsCell = (value: number | undefined) => (): RowValue => ({
    value: typeof value === 'number' ? numeralFormatter(value, '0,0') : '-',
    format: 'none',
    label: typeof value === 'number' ? ' bps' : '',
});

const nameCell = (name: string | undefined) => (): RowValue => ({ value: name ?? '' });

export const proFormaReturnsResolver = (
    returnsData: IProFormaReturnsData | undefined,
    waterfallAnalysis: IWaterfallAnalysis | undefined,
): BlockResolver<ReturnsMetricId> => {
    const summary = waterfallAnalysis?.waterfallSummary;

    return {

        'unlevered.roi': cell(returnsData?.unleveredRoi, 'percentage'),
        'unlevered.emx': equityMultipleCell(returnsData?.netUnleveredEquityMultiple),
        'unlevered.irr': cell(returnsData?.netUnleveredIrr, 'percentage'),

        'levered.roi': cell(returnsData?.leveredRoi, 'percentage'),
        'levered.emx': equityMultipleCell(returnsData?.netLeveredEquityMultiple),
        'levered.irr': cell(returnsData?.netLeveredIrr, 'percentage'),

        'lp.name': nameCell(summary?.lpName),
        'lp.netProceeds': () => ({ value: summary?.lpNetProfit, format: 'currency' }),
        'lp.roi': () => ({ value: summary?.lpRoi, format: 'percentage' }),
        'lp.emx': equityMultipleCell(summary?.lpEmx),
        'lp.irr': () => ({ value: summary?.lpIrr, format: 'percentage' }),

        'gp.name': nameCell(summary?.gp1Name),
        'gp.netProceeds': () => ({ value: summary?.gp1NetProfit, format: 'currency' }),
        'gp.roi': () => ({ value: summary?.gp1Roi, format: 'percentage' }),
        'gp.emx': equityMultipleCell(summary?.gp1Emx),
        'gp.irr': () => ({ value: summary?.gp1Irr, format: 'percentage' }),

        'yieldOnCost.untrended': cell(returnsData?.yieldOnCost?.untrended, 'percentage'),
        'yieldOnCost.trended': cell(returnsData?.yieldOnCost?.trended, 'percentage'),
        'yieldOnCost.sale': cell(returnsData?.yieldOnCost?.sale, 'percentage'),

        'marketCapRate.untrended': cell(returnsData?.marketCapitalizationRate?.current, 'percentage'),
        'marketCapRate.trended': cell(returnsData?.marketCapitalizationRate?.trended, 'percentage'),
        'marketCapRate.sale': cell(returnsData?.marketCapitalizationRate?.onSale, 'percentage'),

        'developmentSpread.untrended': basisPointsCell(returnsData?.developmentSpread?.untrended),
        'developmentSpread.trended': basisPointsCell(returnsData?.developmentSpread?.trended),
        'developmentSpread.sale': basisPointsCell(returnsData?.developmentSpread?.sale),

        'projectProfit.sale': cell(returnsData?.projectProfit ?? undefined, 'currency'),
        'projectProfitMargin.sale': cell(returnsData?.projectProfitMargin ?? undefined, 'percentage'),
    };
};
